import React from 'react';
import { Select, Segmented, Alert, Button, Popconfirm } from 'antd';
import { Store, Trash2, Layers } from 'lucide-react';
import { SectionCard, StatusTag } from '../../../common';

/**
 * Admin-only controls to give a single MODERNO piso its own price list, which replaces the
 * client's list for that store (in the app and in the admin's margin views).
 */
function PriceStoreControls({
    viewMode,
    setViewMode,
    stores,
    selectedStoreId,
    setSelectedStoreId,
    storeListIds,
    hasStoreList,
    hasStoreSegmentation,
    onDelete,
    deleting
}) {
    const withList = stores.filter((s) => storeListIds.has(s._id));
    const selected = stores.find((s) => s._id === selectedStoreId);

    return (
        <div className="space-y-4 mb-4">
            <SectionCard icon={<Layers />} title="Vista de precios">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    <Segmented
                        value={viewMode}
                        onChange={setViewMode}
                        options={[
                            { label: 'Por defecto (cliente)', value: 'default' },
                            { label: 'Excepciones por piso', value: 'exception' }
                        ]}
                    />
                    <span className="text-sm text-surface-500">
                        {viewMode === 'default'
                            ? 'Precios que aplican a todos los pisos de venta del cliente.'
                            : 'Selecciona un piso de venta para darle su propia lista de precios. Reemplaza la del cliente en ese piso.'}
                    </span>
                </div>
            </SectionCard>

            {viewMode === 'exception' && (
                <SectionCard
                    icon={<Store />}
                    title="Piso de venta"
                    extra={
                        <span className="text-xs text-surface-500">
                            {withList.length
                                ? `${withList.length} piso${withList.length === 1 ? '' : 's'} con precios propios`
                                : 'Ningún piso tiene precios propios'}
                        </span>
                    }>
                    <Select
                        showSearch
                        className="w-full"
                        placeholder="Selecciona un piso de venta"
                        value={selectedStoreId}
                        onChange={setSelectedStoreId}
                        optionFilterProp="label"
                        options={stores.map((s) => ({ value: s._id, label: s.name, hasList: storeListIds.has(s._id) }))}
                        optionRender={(opt) => (
                            <span className="flex items-center justify-between gap-2">
                                <span>{opt.label}</span>
                                {opt.data.hasList && <StatusTag tone="warning">Precios propios</StatusTag>}
                            </span>
                        )}
                    />
                    {withList.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-1.5">
                            {withList.map((s) => (
                                <button
                                    key={s._id}
                                    type="button"
                                    onClick={() => setSelectedStoreId(s._id)}
                                    className={`inline-flex h-6 items-center rounded-md px-2 text-xs font-medium transition-colors ${
                                        s._id === selectedStoreId
                                            ? 'bg-warning-solid text-white'
                                            : 'bg-warning-bg text-warning-fg hover:bg-warning-border'
                                    }`}>
                                    {s.name}
                                </button>
                            ))}
                        </div>
                    )}
                </SectionCard>
            )}

            {viewMode === 'exception' && selected && (
                <Alert
                    type={hasStoreList ? 'warning' : 'success'}
                    showIcon
                    message={hasStoreList ? `Precios propios: ${selected.name}` : `Usa los precios del cliente: ${selected.name}`}
                    description={
                        <>
                            {hasStoreList
                                ? 'Este piso usa su propia lista de precios. Guardar creará una nueva versión.'
                                : 'Se muestran los precios del cliente como punto de partida. Guardar creará la lista propia del piso.'}
                            <div className="mt-1">
                                Productos:{' '}
                                {hasStoreSegmentation ? 'segmentación propia del piso' : 'segmentación del cliente'}.
                            </div>
                        </>
                    }
                    action={
                        hasStoreList ? (
                            <Popconfirm
                                title="¿Eliminar los precios propios?"
                                description="El piso volverá a usar la lista de precios del cliente."
                                okText="Eliminar"
                                cancelText="Cancelar"
                                okButtonProps={{ danger: true }}
                                onConfirm={onDelete}>
                                <Button danger size="small" icon={<Trash2 />} loading={deleting}>
                                    Eliminar precios propios
                                </Button>
                            </Popconfirm>
                        ) : null
                    }
                />
            )}
        </div>
    );
}

export default PriceStoreControls;
